/* React wrapper around the verbatim canvas supply-map renderer (supplyMap.ts).
 * Mirrors the Watch3D → WatchViewer pattern: the renderer is framework-free and
 * this component just owns the <canvas>, kicks off the animation loop, and feeds
 * it the latest MapData via a ref so the loop always reads current props without
 * being torn down on every render. Under reduce-motion it paints one static
 * frame instead of running the animation loop. */
import { useEffect, useRef } from 'react';
import { animateSupplyMap, drawSupplyMapStatic, type MapData } from './supplyMap';
import { useReduceMotion } from '../hooks/useReduceMotion';
import { useTheme } from '../hooks/useTheme';

export default function SupplyMapView({ data }: { data: MapData }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dataRef = useRef(data);
  dataRef.current = data;
  const reduce = useReduceMotion();
  // The animated loop repaints every frame and picks up a theme switch on its
  // own; the static frame has to be told to redraw.
  const theme = useTheme();

  // Animate continuously, or paint a single static frame under reduce-motion.
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    if (reduce) { drawSupplyMapStatic(cv, dataRef.current); return; }
    return animateSupplyMap(cv, () => dataRef.current);
  }, [reduce]);

  // Keep the static frame current when the chosen suppliers/markets change.
  useEffect(() => {
    if (reduce && canvasRef.current) drawSupplyMapStatic(canvasRef.current, data);
  }, [reduce, data, theme]);

  return (
    <div className="map-wrap" style={{ marginBottom: 16 }}>
      <canvas ref={canvasRef} width={1200} height={560} />
      <div className="map-legend">
        <span className="row"><span className="dot" style={{ background: 'var(--acc)' }} />Suppliers</span>
        <span className="row"><span className="dot" style={{ background: 'var(--warn)' }} />Assembly</span>
        <span className="row"><span className="dot" style={{ background: 'var(--acc2)' }} />Markets</span>
      </div>
    </div>
  );
}
